import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { BreakdownResult } from '../../types/analytics.types';
import { formatNumber, truncateText } from '../../lib/formatters';
import { CHART_COLORS } from '../../lib/visualizationSelector';

interface BarChartDisplayProps {
  data: BreakdownResult;
  orientation?: 'horizontal' | 'vertical';
}

export function BarChartDisplay({
  data,
  orientation = 'horizontal',
}: BarChartDisplayProps) {
  const { groups } = data;

  const chartData = groups.map((group, index) => ({
    name: group.label,
    value: group.count,
    percentage: group.percentage,
    fill: CHART_COLORS.categorical[index % CHART_COLORS.categorical.length],
  }));

  const isHorizontal = orientation === 'horizontal';

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="bg-popover border rounded-lg shadow-lg p-3">
          <p className="font-medium text-foreground">{item.name}</p>
          <p className="text-sm text-muted-foreground">
            {formatNumber(item.value)} patients ({item.percentage.toFixed(1)}%)
          </p>
        </div>
      );
    }
    return null;
  };

  if (isHorizontal) {
    return (
      <ResponsiveContainer width="100%" height={Math.max(250, groups.length * 40)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={(value) => formatNumber(value)}
            fontSize={12}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={90}
            tickFormatter={(value) => truncateText(value, 15)}
            fontSize={12}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={chartData}
        margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="name"
          tickFormatter={(value) => truncateText(value, 10)}
          fontSize={12}
          angle={-45}
          textAnchor="end"
          height={60}
        />
        <YAxis
          tickFormatter={(value) => formatNumber(value)}
          fontSize={12}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.fill} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
